<template>
    <main>
        <div class="flex flex-col mx-auto max-w-6xl sm:px-6 lg:flex-col-reverse lg:px-8">
            <div class="relative overflow-hidden">
                <div class="relative pb-10 lg:pb-24">
                    <div class="mx-auto max-w-7xl">
                        <div class="px-2 py-3 sm:py-12 sm:px-6 lg:px-8">
                            <div class="max-w-3xl mx-auto text-center">
                                <h1 class="text-3xl sm:text-7xl lg:text-8xl font-extrabold text-gray-900 sm:tracking-tight">Throbo</h1>
                                <h2 class="mt-2 sm:mt-5 text-lg font-extrabold text-gray-900 sm:text-2xl sm:tracking-tight lg:text-3xl">A new way to monetize your followers</h2>
                                <p class="hidden sm:block mt-3 max-w-2xl mx-auto text-sm md:text-xl text-gray-700 sm:mt-7">
                                    Our platform allows you to create and accept challenges, and with the help of our community, you'll have all the motivation you need to reach your goals and make money doing it.
                                </p>
                                <p class="sm:hidden mt-3 max-w-2xl mx-auto text-md md:text-xl text-gray-700 sm:mt-7">Our platform lets you create and accept challenges, providing motivation to achieve your goals and earn money.</p>
                            </div>
                        </div>

                        <!-- Screenshots Grid Section -->
                        <div v-if="!isMobile" class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div v-for="(step, index) in steps" :key="index" class="group relative rounded-lg overflow-hidden shadow-md">
                                <img :src="step.image" :alt="'Screenshot ' + (index + 1)" class="w-full h-auto object-cover" />
                                <div class="absolute bottom-0 left-0 right-0 caption-bar">
                                    <h3 class="text-xl font-semibold text-white">{{ step.title }}</h3>
                                    <p class="text-white">{{ step.description }}</p>
                                </div>
                                <div class="absolute bottom-0 left-0 right-0 caption-bar-dark"></div>
                                <div class="absolute bottom-0 left-0 right-0 caption-bar-light"></div>
                                <div class="absolute bottom-0 left-0 right-0 caption-bar-bottom"></div>
                            </div>
                        </div>
                        <div v-else class="carousel-container">
                            <!-- Carousel layout for mobile views -->
                            <div v-for="(step, index) in steps" :key="'mobile-' + index" class="carousel-slide-wrapper">
                                <div class="carousel-slide">
                                    <img :src="step.image" :alt="'Screenshot ' + (index + 1)" class="w-full h-auto object-cover" />
                                    <div class="caption">
                                        <h3 class="font-bold pb-2 text-lg">{{ step.title }}</h3>
                                        <p class="text-sm font-semibold">{{ step.description }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="mt-8 px-4 py-6 bg-white shadow-md rounded-lg">
                            <h2 class="text-xl font-semibold text-gray-900 mb-4">Payout Options Made Easy</h2>
                            <p class="text-gray-700 mb-6">At Throbo.com, we ensure receiving your earnings is as smooth as completing challenges. Select from these secure and easy payout methods:</p>
                            <ul class="list-disc pl-5 space-y-2">
                                <li class="text-gray-600"><strong>CashApp:</strong> Get your funds sent directly to your CashApp account, ensuring fast and convenient access.</li>
                                <li class="text-gray-600"><strong>Venmo:</strong> Prefer Venmo? We’ve got you covered. Transfer your earnings to your Venmo account with just a few taps.</li>
                                <li class="text-gray-600"><strong>ACH Bank Transfer:</strong> Opt for an ACH Bank Transfer to have your funds deposited directly into your bank account.</li>
                            </ul>
                        </div>

                        <div class="mt-8 px-4 py-6 bg-white shadow-md rounded-lg">
                            <h2 class="text-xl font-semibold text-gray-900 mb-4">Challenge Visibility</h2>
                            <p class="text-gray-700 mb-6">
                                Even if no tokens are pledged towards a challenge, rest assured it will still be visible to our entire community upon completion. This ensures that all your efforts are recognized and appreciated, fostering a supportive and engaging environment for everyone at
                                Throbo.com.
                            </p>
                        </div>

                        <div class="mt-10 mx-auto max-w-2xl lg:max-w-4xl bg-white sm:rounded-3xl px-4 py-10 sm:py-12 sm:px-6 lg:px-8">
                            <div class="max-w-3xl mx-auto text-center">
                                <h2 class="text-2xl font-extrabold text-gray-900 sm:tracking-tight lg:text-3xl">Who are you challenging?</h2>
                                <div class="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
                                    <a href="/challenges/personal/create" class="flex items-center justify-center w-full px-8 py-3 text-base font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 md:py-4 md:text-lg md:px-10"> YOURSELF </a>
                                    <a href="/challenges/targeted/create" class="mt-3 rounded-md shadow sm:mt-0 sm:ml-5 flex items-center justify-center w-full px-8 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 md:py-4 md:text-lg md:px-10"> FRIEND </a>
                                    <a href="/challenges/create" class="mt-3 rounded-md shadow sm:mt-0 sm:ml-5 flex items-center justify-center w-full px-8 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 md:py-4 md:text-lg md:px-10"> ANYONE </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import SocialLogin from "@/Pages/Auth/SocialLogin"
import JetValidationErrors from "@/Jetstream/ValidationErrors"
import Login from "@/Pages/Auth/Login"
import ShareModal from "@/Components/ShareModal"
import Like from "@/Components/React"
import Tooltip from "@/Components/Tooltip"
import FrontPageCard from "@/Pages/FrontPage/PostCard"
import FrontPageLayout from "@/Layouts/FrontPageLayout"
import InstallAppButton from "@/Components/Buttons/InstallAppButton"
import JetButton from "@/Jetstream/Button"

export default {
    name: "Welcome",

    layout: FrontPageLayout,

    components: { JetButton, InstallAppButton, FrontPageCard, Tooltip, Like, ShareModal, Login, SocialLogin, JetValidationErrors },

    mounted() {},

    props: {
        canLogin: Boolean,
        canRegister: Boolean,
        posts: Array,
    },

    computed: {
        isMobile() {
            return window.innerWidth <= 768 // Example breakpoint for mobile devices
        },
    },

    data() {
        return {
            form: this.$inertia.form({
                name: "",
                email: "",
                password: "",
                password_confirmation: "",
                terms: true,
            }),
            showSharingModal: false,
            currentSlide: 0, // For carousel functionality
            steps: [
                {
                    title: "1. Launch Your Challenge",
                    description: "Create and showcase your challenge with an engaging photo to attract competitors.",
                    image: "/images/IMG_4013.PNG",
                },
                {
                    title: "2. Kickstart with a Pledge",
                    description: "Initiate the challenge by pledging tokens and inviting others to join.",
                    image: "/images/IMG_4139.PNG",
                },
                {
                    title: "3. Rally Your Supporters",
                    description: "Encourage followers to pledge tokens, turning the challenge into a community effort.",
                    image: "/images/IMG_4016.PNG",
                },
                {
                    title: "4. Submission & Verification",
                    description: "Competitors submit evidence of completion for approval, ensuring fairness.",
                    image: "/images/IMG_4020.PNG",
                },
                {
                    title: "5. Approval for Fair Play",
                    description: "Submissions are approved by challengers and then verified for integrity by Throbo.",
                    image: "/images/IMG_4033.PNG",
                },
                {
                    title: "6. Reward Distribution",
                    description: "After approval, competitors earn 80% of the tokens' value; the remaining 20% supports Throbo.",
                    image: "/images/IMG_4140.PNG",
                },
            ],
        }
    },

    methods: {
        submit() {
            this.form.post(this.route("register"), {
                onFinish: () => this.form.reset("password", "password_confirmation"),
            })
        },
    },
}
</script>

<style scoped>
/* Styles for the image grid */
.grid img {
    width: 100%;
    height: auto;
    object-fit: cover;
    box-sizing: border-box;
    margin-top: -47px;
    border-radius: 0.5rem;
}
.grid .group {
    max-height: 650px;
    border-radius: 0.5rem;
    border: 15px solid #ccc;
    overflow: hidden;
}

/* Relative positioning for the container to position the caption */
.relative {
    position: relative;
}

/* Absolute positioning for the caption bar at the bottom of the image */
.caption-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(99, 102, 241, 0.8);
    color: white;
    padding: 1rem;
    border-radius: 0 0 0.5rem 0.5rem;
    min-height: 140px;
    z-index: 3;
}
.caption-bar-dark {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 0 0 0.5rem 0.5rem;
    min-height: 140px;
    z-index: 2;
}
.caption-bar-light {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 0 0 0.5rem 0.5rem;
    min-height: 140px;
    z-index: 1;
}
.caption-bar-bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    border: 2px solid #ccc;
    border-top: none;
    min-height: 20px;
    z-index: 1;
}

.carousel-container {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding: 20px;
    box-sizing: border-box;
}
.carousel-slide-wrapper {
    max-height: 600px;
    position: relative;
    flex: 0 0 calc(100% - 20px);
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    border: none;
    padding-left: 20px;
}
.carousel-slide {
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
    border-radius: 0.5rem;
    border: none;
    box-shadow:
        0 4px 6px rgba(0, 0, 0, 0.2),
        0 2px 4px rgba(0, 0, 0, 0.12),
        0 -4px 6px rgba(0, 0, 0, 0.1); /* Top shadow */
}
.carousel-slide img {
    margin-top: -40px;
    max-width: 100%;
    height: auto;
    object-fit: contain;
    max-height: 100vh;
    display: block;
}
.caption {
    min-height: 120px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(99, 102, 241, 0.97);
    color: white;
    padding: 1rem;
    border-radius: 0 0 0.5rem 0.5rem;
    z-index: 3;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>
